import Dexie, { type EntityTable } from 'dexie';
import type { Cadence, HabitStatus } from '@selflevel/domain';

/**
 * EL ALMACÉN LOCAL. Aquí vive todo lo del usuario, y aquí funciona la app entera
 * sin red: escribir, leer, calcular niveles, ver el año. El servidor es una
 * copia de seguridad que además sincroniza entre dispositivos, no la fuente.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * DESVIACIÓN DEL PRD, medida: Dexie directo en vez de RxDB.
 *
 * El PRD eligió RxDB 17.5 con almacén Dexie por debajo, y su propio apartado 6.2
 * aclara que **RxDB solo sirve el camino de lectura**: la outbox de escritura se
 * escribe a mano de todos modos. Es decir, lo único que aportaba era consultas
 * reactivas.
 *
 * Al medir el bundle de la fase 0 salió **122,67 kB de los 180** del presupuesto
 * con React y el router y SIN RxDB, que no baja de ~100 kB gzip. El presupuesto
 * no es un consejo: es la lección directa del benchmark, que carga 846 kB a todo
 * el mundo, incluidos 145 kB de panel de administración para quien no es
 * administrador.
 *
 * `Dexie.liveQuery` da las consultas reactivas por ~25 kB. Se conserva todo lo
 * demás del PRD: IndexedDB, offline-first real, outbox propia, ids deterministas
 * y HLC. Lo que se va es una capa que aquí no aportaba.
 * ───────────────────────────────────────────────────────────────────────────
 */

export type LifeArea = {
  id: string;
  name: string;
  /** Una de las 28 marcas. */
  marca: string;
  /** `--dim-1` … `--dim-6`. */
  colorToken: string;
  sortOrder: number;
  archivedAt: string | null;
  /** A qué área de la rueda de la vida va (`fisica`…). Sin valor, la propuesta por su marca. */
  wheelKey?: string | null;
};

export type IdentityStatement = {
  id: string;
  areaId: string;
  /** «Soy alguien que…». Lo escribe el usuario y **nunca sale de aquí**. */
  text: string;
  adoptedAt: string;
  retiredAt: string | null;
};

export type Goal = {
  id: string;
  areaId: string;
  identityId: string | null;
  title: string;
  horizon: 'anual' | 'trimestral' | 'sin-fecha';
  targetDate: string | null;
  status: 'activa' | 'cumplida' | 'abandonada';
  completedAt: string | null;
  /** Día lógico del cumplimiento (v5): el que puntúa. Null en lo cumplido antes. */
  localDate?: string | null;
  createdAt: string;
  deletedAt?: string | null;
};

export type GoalMilestone = {
  id: string;
  goalId: string;
  title: string;
  doneAt: string | null;
  /** Día lógico en que se marcó (v5): el que puntúa. */
  localDate?: string | null;
  sortOrder: number;
  deletedAt?: string | null;
};

export type Habit = {
  id: string;
  areaId: string;
  identityId: string | null;
  goalId: string | null;
  name: string;
  marca: string;
  /** Para quien quiera precisar qué es. Opcional. */
  description?: string | null;
  /** Otras áreas que trabaja, además de `areaId` (como mucho dos, reglamento v4). */
  extraAreaIds?: string[];
  cadence: Cadence;
  /** Objetivo numérico por ocasión, si el hábito es cuantificable. */
  target: number | null;
  unit: string | null;
  reminderTime: string | null;
  sortOrder: number;
  archivedAt: string | null;
  createdAt: string;
};

/**
 * LA TABLA CALIENTE. Su `id` es determinista —`uuidv5(user:habit:fecha)`— y por
 * eso dos dispositivos sin cobertura que marquen lo mismo el mismo día producen
 * **una fila**, no dos.
 */
export type HabitEntry = {
  id: string;
  habitId: string;
  localDate: string;
  status: HabitStatus;
  value: number | null;
  /** Zona en la que se escribió. Se guarda para el perdón de racha por viaje. */
  tz: string;
  clientTs: string;
  /** Si el reloj del dispositivo era plausible. Lo no atestado no da puntos. */
  attested: boolean;
  /** `propio` o la clave del proveedor que la generó. Siempre visible al usuario. */
  source?: string;
  externalEventId?: string | null;
  updatedAt: string;
  deletedAt: string | null;
};

/** Append-only: un hecho, no un estado. Corregir crea otra con `supersedesId`. */
export type FocusSession = {
  id: string;
  habitId: string | null;
  areaId: string | null;
  label: string;
  localDate: string;
  startedAt: string;
  endedAt: string;
  plannedMinutes: number;
  actualMinutes: number;
  interrupted: boolean;
  supersedesId: string | null;
  tz: string;
  attested: boolean;
};

export type JournalEntry = {
  id: string;
  localDate: string;
  /** En claro en local. Al sincronizar viaja cifrado de extremo a extremo. */
  body: string;
  tags: string[];
  tz: string;
  updatedAt: string;
  deletedAt: string | null;
};

/** Append-only. */
export type MoodLog = {
  id: string;
  localDate: string;
  /** 1 a 5. */
  mood: number;
  /** 1 a 5. */
  energy: number;
  note: string;
  notedAt: string;
  tz: string;
  attested: boolean;
};

export type ReviewSession = {
  id: string;
  periodType: 'weekly' | 'monthly';
  /** `2026-W37` o `2026-09`. */
  periodKey: string;
  answers: Record<string, string>;
  localDate: string;
  completedAt: string | null;
  attested: boolean;
};

export type ReviewAction = {
  id: string;
  reviewId: string;
  text: string;
  doneAt: string | null;
};

/** Una cosa que hacer. `plannedDate: null` = la bandeja «Sin día». */
export type Task = {
  id: string;
  title: string;
  plannedDate: string | null;
  areaId: string | null;
  goalId: string | null;
  doneAt: string | null;
  doneLocalDate: string | null;
  sortOrder: number;
  tz: string;
  attested: boolean;
  createdAt: string;
  deletedAt: string | null;
};

/** El plan de un día (id determinista por usuario y día). */
export type DayPlan = {
  id: string;
  localDate: string;
  mission: string;
  pillarAreaId: string | null;
  frogTaskId: string | null;
  projectGoalIds: string[];
  ritualSealedAt: string | null;
  tz: string;
  attested: boolean;
  deletedAt: string | null;
};

/** El plan de una semana ISO (id determinista por usuario y semana). */
export type WeekPlan = {
  id: string;
  weekKey: string;
  reward: string;
  deletedAt: string | null;
};

/**
 * La rueda de la vida de un mes (id determinista por usuario y mes). `scores` son
 * las respuestas al cuestionario: `{ "fisica.apariencia": { p: 7, f: 9 } }`.
 */
export type LifeWheel = {
  id: string;
  monthKey: string;
  scores: Record<string, { p: number; f?: number }>;
  sealedAt: string | null;
  localDate: string | null;
  tz: string;
  attested: boolean;
  deletedAt: string | null;
};

/** Un sueño (bloque 2): el rumbo largo, con el tiempo que el usuario estima. */
export type Dream = {
  id: string;
  areaId: string;
  title: string;
  why: string | null;
  estimatedMonths: number;
  startedOn: string;
  status: 'activa' | 'cumplida' | 'abandonada';
  /** `monolito` · `gema` · `constelacion` · `medalla`. Se elige al crearlo. */
  trophyForm: string;
  trophyMeaning: string | null;
  completedAt: string | null;
  localDate: string | null;
  tz: string;
  createdAt: string;
  deletedAt: string | null;
};

/** Un paso de un sueño. No puntúa: aclara su trofeo. */
export type DreamStep = {
  id: string;
  dreamId: string;
  title: string;
  dueOn: string | null;
  doneAt: string | null;
  sortOrder: number;
  suggestedBy: 'usuario' | 'copiloto' | 'plantilla';
  deletedAt: string | null;
};

/** Temporada cerrada y sellada. **Nunca se borra y nunca se resta.** */
export type SealedSeasonRow = {
  key: string;
  label: string;
  from: string;
  to: string;
  points: number;
  ligaKey: string;
  ligaName: string;
  milestones: number;
  sealedAt: string;
};

/** Hito desbloqueado. Conjunto que solo crece: sin `deletedAt` a propósito. */
export type MilestoneUnlock = {
  key: string;
  n: number;
  name: string;
  unlockedAt: string;
  localDate: string;
  attested: boolean;
};

export type Settings = {
  id: 'unica';
  displayName: string;
  /** Minutos desde medianoche. 240 = «mi día empieza a las 04:00». */
  dayStartOffsetMin: number;
  tz: string;
  locale: 'es' | 'en';
  theme: 'dark' | 'light';
  /**
   * COLUMNA HEREDADA. Hubo dos pieles —«expresiva» y «sobria»— y la sobria se
   * retiró el 21 de septiembre de 2026: ver la cabecera de `features/progreso`.
   *
   * El tipo se cierra a un solo valor a propósito. Dejarlo como unión de dos
   * sería un tipo que miente —no hay adónde ir con «sobria»—, y quitarlo del
   * todo obliga a tocar el contrato de sincronización, la tabla de Postgres y
   * una migración, y las migraciones de este proyecto son una puerta de la
   * fase 0 con vuelta atrás probada. El VPS todavía no existe, así que no hay
   * dato de nadie que migrar: se borra de arriba abajo cuando haya sitio donde
   * probar el rollback. Mientras tanto la columna viaja y vale siempre lo
   * mismo, que cuesta cero y no rompe nada.
   */
  skin: 'expresiva';
  transparency: 'standard' | 'reduced';
  focusDefaultMinutes: number;
  /** Descanso corto, descanso largo y pomodoros por tanda (spec 2026-10-28). Del dispositivo. */
  focusBreakMinutes: number;
  focusLongBreakMinutes: number;
  focusCyclesBeforeLong: number;
  /**
   * Para el memento mori. NO sube al servidor (`subirAjustesSiHaceFalta` manda una
   * lista cerrada): es un dato personal y aquí solo sirve para dibujar.
   */
  birthDate: string | null;
  journalPrivateMode: boolean;
  onboardedAt: string | null;
  /** Suelo sellado del nivel general. Nunca baja de aquí. */
  levelFloor: number;
  lastSeenSeason: string | null;
  lastSeenMilestone: string | null;
};

/**
 * Mutación pendiente de enviar. La outbox nunca guarda nada que no esté ya en su
 * tabla: es la regla que hace recuperable cualquier pérdida de IndexedDB.
 */
export type OutboxItem = {
  id: string;
  seq: number;
  type: string;
  /**
   * La fila **completa**. El servidor necesita todas las columnas obligatorias
   * para poder hacer el INSERT, y no puede inventárselas.
   */
  row: Record<string, unknown>;
  /**
   * Y **solo lo que esta escritura cambió**. Es lo que hace posible el LWW por
   * campo: si este dispositivo cambió `status` y otro `value`, cada uno gana en
   * el suyo en vez de perder uno entero. Declarar «todo» convierte el LWW por
   * campo en LWW por fila sin que se note.
   */
  fields: string[];
  hlc: string;
  createdAt: string;
  attempts: number;
  /** Cuándo puede volver a intentarse. Es el backoff, persistido. */
  nextAttemptMs: number;
  lastError: string | null;
  /** `pending` · `dead` (retirada: el servidor dijo que no reintentara). */
  state: 'pending' | 'dead';
};

/**
 * El ledger que calcula el SERVIDOR. Llega en solo lectura y sirve para dos cosas:
 * el suelo del nivel —que no vive en este dispositivo, así que reinstalar no lo
 * pierde— y detectar divergencia con el cálculo local.
 */
export type LedgerRow = {
  id: string;
  rulesetVersion: number;
  totalPoints: number;
  dimensions: { key: string; points: number; level: { level: number } }[];
  level: number;
  levelFloor: number;
  computedAt: string;
};

/**
 * Lo que importó un proveedor externo, con su procedencia (PRD 14.5). **Nunca se
 * mezcla con lo que el usuario escribió**: desconectar Google retira sus filas sin
 * dejar residuos y sin tocar el historial propio.
 */
export type ExternalEventRow = {
  id: string;
  provider: string;
  externalId: string;
  kind: string;
  title: string;
  calendarLabel: string | null;
  startsAt: string | null;
  endsAt: string | null;
  localDate: string | null;
  allDay: boolean;
  tz: string | null;
  importedAt: string;
};

export type Meta = {
  key: string;
  value: unknown;
};

class SelflevelDb extends Dexie {
  lifeAreas!: EntityTable<LifeArea, 'id'>;
  identities!: EntityTable<IdentityStatement, 'id'>;
  goals!: EntityTable<Goal, 'id'>;
  goalMilestones!: EntityTable<GoalMilestone, 'id'>;
  habits!: EntityTable<Habit, 'id'>;
  habitEntries!: EntityTable<HabitEntry, 'id'>;
  focusSessions!: EntityTable<FocusSession, 'id'>;
  journalEntries!: EntityTable<JournalEntry, 'id'>;
  moodLogs!: EntityTable<MoodLog, 'id'>;
  reviews!: EntityTable<ReviewSession, 'id'>;
  reviewActions!: EntityTable<ReviewAction, 'id'>;
  seasons!: EntityTable<SealedSeasonRow, 'key'>;
  milestones!: EntityTable<MilestoneUnlock, 'key'>;
  settings!: EntityTable<Settings, 'id'>;
  outbox!: EntityTable<OutboxItem, 'id'>;
  meta!: EntityTable<Meta, 'key'>;
  ledger!: EntityTable<LedgerRow, 'id'>;
  externalEvents!: EntityTable<ExternalEventRow, 'id'>;
  tasks!: EntityTable<Task, 'id'>;
  dayPlans!: EntityTable<DayPlan, 'id'>;
  weekPlans!: EntityTable<WeekPlan, 'id'>;
  lifeWheels!: EntityTable<LifeWheel, 'id'>;
  dreams!: EntityTable<Dream, 'id'>;
  dreamSteps!: EntityTable<DreamStep, 'id'>;

  constructor() {
    super('selflevel');

    /**
     * Los índices son los del PRD 9.3, que no son genéricos: salen de las
     * consultas reales. `[habitId+localDate]` sirve las rachas y el mapa de
     * calor; `localDate` sirve la vista de Hoy.
     */
    this.version(1).stores({
      lifeAreas: 'id, sortOrder, archivedAt',
      identities: 'id, areaId, retiredAt',
      goals: 'id, areaId, status',
      goalMilestones: 'id, goalId, sortOrder',
      habits: 'id, areaId, identityId, sortOrder, archivedAt',
      habitEntries: 'id, habitId, localDate, [habitId+localDate], deletedAt',
      focusSessions: 'id, localDate, startedAt, habitId',
      journalEntries: 'id, localDate, deletedAt',
      moodLogs: 'id, localDate',
      reviews: 'id, [periodType+periodKey], completedAt',
      reviewActions: 'id, reviewId, doneAt',
      seasons: 'key, to',
      milestones: 'key, n, unlockedAt',
      settings: 'id',
      outbox: 'id, seq, state',
      meta: 'key',
    });

    /**
     * Versión 2: las dos colecciones que escribe el servidor y el cliente solo lee.
     *
     * No hay migración de datos porque no hay datos que migrar: las dos tablas
     * nacen vacías y se llenan con el primer `pull`. Una versión nueva de Dexie es
     * de todos modos obligatoria —añadir un almacén a IndexedDB no se puede hacer
     * fuera de una transacción de versión— y hacerlo así significa que un usuario
     * que ya tenía la app no pierde nada al actualizar.
     */
    this.version(2)
      .stores({
        ledger: 'id',
        externalEvents: 'id, localDate, provider, [provider+localDate]',
      })
      .upgrade(async (tx) => {
        /**
         * ═════════════════════════════════════════════════════════════════════
         * LA OUTBOX SÍ NECESITA MIGRACIÓN, Y ES LA ÚNICA TABLA QUE LA NECESITA.
         *
         * Las demás se reconstruyen solas: el servidor las tiene y el próximo `pull`
         * las trae. **La outbox no**: es exactamente lo que el servidor todavía no
         * tiene, así que una fila con la forma vieja es un dato del usuario perdido.
         *
         * Y se pierde del peor modo posible: el servidor rechaza la mutación por forma
         * inválida —«expected record, received undefined»—, el cliente la marca como no
         * reintentable, y al usuario le sale «183 cambios no pudieron guardarse». Pasó
         * de verdad al abrir la app con datos de una versión anterior, y por eso está
         * escrito aquí y no en el mensaje de un commit.
         *
         * Regla, entonces: **cambiar la forma de la outbox obliga a migrarla.** No es
         * una tabla de caché.
         * ═════════════════════════════════════════════════════════════════════
         */
        await tx
          .table('outbox')
          .toCollection()
          .modify((item: Record<string, unknown>) => {
            if (item['row'] === undefined && item['payload'] !== undefined) {
              item['row'] = item['payload'];
              delete item['payload'];
            }
            item['fields'] ??= [];
            item['nextAttemptMs'] ??= 0;
            /**
             * `sending` ya no existe: era un estado que se quedaba pegado si la pestaña
             * moría a mitad de envío, y con la idempotencia del servidor no aportaba
             * nada. Lo que estuviera ahí vuelve a la cola.
             */
            if (item['state'] === 'sending') item['state'] = 'pending';
          });
      });

    /**
     * Versión 3: las tres colecciones de «Hoy con intención». Nacen vacías y se llenan
     * con el uso o con el primer `pull`; la outbox no cambia de forma, así que no hay
     * nada que migrar (ver la regla de la versión 2).
     */
    this.version(3).stores({
      tasks: 'id, plannedDate, doneLocalDate, deletedAt',
      dayPlans: 'id, localDate',
      weekPlans: 'id, weekKey',
    });

    /** Versión 4: la rueda de la vida (bloque 3). Nace vacía, como las de la 3. */
    this.version(4).stores({
      lifeWheels: 'id, monthKey',
    });

    /** Versión 5: sueños y sus pasos (bloque 2). Nacen vacías, como las de la 3 y la 4. */
    this.version(5).stores({
      dreams: 'id, areaId, status',
      dreamSteps: 'id, dreamId, sortOrder',
    });
  }
}

export const db = new SelflevelDb();

/**
 * Borra todo lo local. Se usa en el re-bootstrap del PRD 15.4 —cuando falta el
 * marcador de integridad o la versión del esquema no cuadra— y en «borrar mis
 * datos» de Ajustes.
 *
 * Es seguro **solo** porque se cumple la regla de la outbox: nunca contiene
 * nada que no esté ya en el servidor o en camino.
 */
export async function wipeLocal(): Promise<void> {
  await db.delete();
  await db.open();
}
